
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';
import DownloadExcel from 'vue-json-excel/JsonExcel.vue';

// --- Models ---
import { JobCard, JobType, JobCardLineItem, JobCardVehicleOdometers } from '@/models/JobCard.model';

// --- Services ---
import AppService from '@/services/app';
import JobCardService from '@/services/jobCard';
import VehicleService from '@/services/vehicle';

// --- Third Party ---
import dayjs from 'dayjs';

@Component({
  components: { 
    DownloadExcel,
  },
})
export default class SearchJobCards extends Vue {

  // used by Filter Area
  selectedTypeIds: number[] = [];
  searchVehicleId: number = 0;
  startDatePicker: boolean = false;
  startDate: string = dayjs().startOf('month').format('YYYY-MM-DD');
  endDatePicker: boolean = false;
  endDate: string = dayjs().format('YYYY-MM-DD');

  searchClicked: boolean = false;

  searchResults: JobCard[] = [];
  vehicleOdometers: JobCardVehicleOdometers = Object.assign({}, this.$store.getters['jobCards/emptyVehicleOdometers']);
  lineItems: JobCardLineItem[] = [];

  expandDelay: boolean = false;

  // #region Functions to display list data on page load
    headers() {
      return [
        {
          text: '#',
          value: 'id',
        },
        {
          text: 'Vehicle(s)',
          value: 'vehicleCodes',
        },
        {
          text: 'Start Date',
          value: 'startDate',
        },
        {
          text: 'Type',
          value: 'jobType',
        },
        {
          sortable: false,
          text: 'Description',
          value: 'description',
        },
        {
          text: 'Completion ETA',
          value: 'completionEta',
        },
        {
          sortable: false,
          text: 'Original ETA',
          value: 'originalEta',
        },
        {
          sortable: false,
          text: 'Reason',
          value: 'adjustedEtaReason',
        },
        {
          text: 'Completed On',
          value: 'closedAt',
        },
        {
          text: 'Approved On',
          value: 'approvedAt',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    lineItemHeaders() {
      return [
        {
          text: 'Description',
          value: 'description',
        },
        {
          text: 'Cost',
          value: 'cost',
          align: 'end',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    customSort(items: any[], sortBy: string[], sortDesc: boolean[], locale: string) {
      // If the result is negative, a is sorted before b.
      // If the result is positive, b is sorted before a.
      // If the result is 0, no changes are done with the sort order of the two values.

      switch (sortBy[0]) {
        case 'vehicleCodes':
          if (sortDesc[0]) {
            items.sort((a: JobCard, b: JobCard) => a.vehicle1Code.toLowerCase().localeCompare(b.vehicle1Code.toLowerCase()));
          } else {
            items.sort((a: JobCard, b: JobCard) => b.vehicle1Code.toLowerCase().localeCompare(a.vehicle1Code.toLowerCase()));
          }
          break;

        case 'startDate':
          if (sortDesc[0]) {
            items.sort((a: JobCard, b: JobCard) => a.startDate.localeCompare(b.startDate));
          } else {
            items.sort((a: JobCard, b: JobCard) => b.startDate.localeCompare(a.startDate));
          }
          break;

        case 'jobType':
          if (sortDesc[0]) {
            items.sort((a: JobCard, b: JobCard) => (a.secondaryJobType ? a.secondaryJobType : a.jobType).toLowerCase().localeCompare((b.secondaryJobType ? b.secondaryJobType : b.jobType).toLowerCase()));
          } else {
            items.sort((a: JobCard, b: JobCard) => (b.secondaryJobType ? b.secondaryJobType : b.jobType).toLowerCase().localeCompare((a.secondaryJobType ? a.secondaryJobType : a.jobType).toLowerCase()));
          }
          break;

        case 'completionEta':
          if (sortDesc[0]) {
            items.sort((a: JobCard, b: JobCard) => (a.adjustedEta ? a.adjustedEta : a.originalEta).localeCompare((b.adjustedEta ? b.adjustedEta : b.originalEta)));
          } else {
            items.sort((a: JobCard, b: JobCard) => (b.adjustedEta ? b.adjustedEta : b.originalEta).localeCompare((a.adjustedEta ? a.adjustedEta : a.originalEta)));
          }
          break;
        
        case 'closedAt':
          if (sortDesc[0]) {
            items.sort((a: JobCard, b: JobCard) => (a.closedAt ? a.closedAt : '').localeCompare((b.closedAt ? b.closedAt : '')));
          } else {
            items.sort((a: JobCard, b: JobCard) => (b.closedAt ? b.closedAt : '').localeCompare((a.closedAt ? a.closedAt : '')));
          }
          break;

         case 'approvedAt':
          if (sortDesc[0]) {
            items.sort((a: JobCard, b: JobCard) => (a.approvedAt ? a.approvedAt : '').localeCompare((b.approvedAt ? b.approvedAt : '')));
          } else {
            items.sort((a: JobCard, b: JobCard) => (b.approvedAt ? b.approvedAt : '').localeCompare((a.approvedAt ? a.approvedAt : '')));
          }
          break;
      }

      return items;
    }

    async beforeMount() {
      await JobCardService.listTypes(false);
      await VehicleService.listTypes(false);
      await VehicleService.listActive(false);
      await VehicleService.listDisposed(false);
    }

    canSearch() {
      // At least something MUST be selected
      return this.selectedTypeIds.length > 0 || this.searchVehicleId > 0 || this.startDate || this.endDate;
    }

    toggleStatus(statusId: number) {
      this.$nextTick(() => {
        if (this.selectedTypeIds.includes(statusId)) {
          // remove this one
          this.selectedTypeIds.splice(this.selectedTypeIds.indexOf(statusId), 1);
        } else {
          //add this one
          this.selectedTypeIds.push(statusId);
        }
      })
    }

    getSelectionIcon(statusId: number) {
      if (this.selectedTypeIds.includes(statusId)) {
        // show selected tickbox
        return 'mdi-checkbox-marked';
      } else {
        // show empty tickbox
        return 'mdi-checkbox-blank-outline';
      }
    }

    async search() {
      try {
        this.searchClicked = true;

        const response = await JobCardService.search({ params: {
          jobTypeIds: this.selectedTypeIds.length > 0 ? this.selectedTypeIds.join(',') : undefined,
          vehicleId: this.searchVehicleId,
          startDate: this.startDate,
          endDate: this.endDate,
        }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
            this.searchResults = [];
          } else {
            this.searchResults = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_JobCards.vue - search - 938 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.searchClicked = false;
      }
    }

    async listLineItems(jobCard: JobCard) {
      try {
        this.lineItems = [];
        const response = await JobCardService.listLineItemsByJobCard({ params: { jobCardId: jobCard.id }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            if (response.data.message !== 'No Data Found') {
              AppService.errorHandler(response.data.message);
            }
            this.lineItems = [];
          } else {
            this.lineItems = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.lineItems = [];
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_JobCards.vue - listLineItems - 971 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    async getJobCardVehicleOdometers(jobCard: JobCard) {
      try {
        this.vehicleOdometers = Object.assign({}, this.$store.getters['jobCards/emptyVehicleOdometers']);
        const response = await JobCardService.getJobCardVehicleOdometers({ params: { jobCardId: jobCard.id }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            if (response.data.message !== 'No Data Found') {
              AppService.errorHandler(response.data.message);
            }
            this.vehicleOdometers = Object.assign({}, this.$store.getters['jobCards/emptyVehicleOdometers']);
          } else {
            this.vehicleOdometers = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.vehicleOdometers = Object.assign({}, this.$store.getters['jobCards/emptyVehicleOdometers']);
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_JobCards.vue - getJobCardVehicleOdometers - 1731 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    jobTypeRequiresOdo(item: JobCard) {
      return this.$store.getters['jobCards/jobTypes'].find((type: JobType) => type.id === item.jobTypeId).requiresOdo;
    }

    async expandJobCard(item: JobCard, expand: any) {
      this.lineItems = [];
      this.vehicleOdometers = Object.assign({}, this.$store.getters['jobCards/emptyVehicleOdometers']);

      if (item.jobType === 'Service' || item.jobType === 'Wheel Alignment') {
        // go fetch the vehicle ODO info for this load
        try {
          this.expandDelay = true;
          await this.getJobCardVehicleOdometers(item);
        } finally {
          this.expandDelay = false;
        }
      } else if (item.jobType === 'Maintenance' && item.lineItemCount && item.lineItemCount > 0) {
        // go fetch the lineItems for this load
        try {
          this.expandDelay = true;
          await this.listLineItems(item);
        } finally {
          this.expandDelay = false;
        }
      }

      expand(true);
    }

    closeRow(expand: any) {
      expand(false);
    }

    getLineItemTotal() {
      return this.lineItems.length > 0 ? this.lineItems.map((item: JobCardLineItem) => item.cost).reduce((prev, next) => prev + next) : 0.00;
    }

    async printPdf(item: JobCard) {
      try {
        const response = await JobCardService.printPdf({
          id: item.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            // WHAT NOW?
            window.open(response.data.message);            
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_JobCards.vue - printPdf - 1025 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      }
    }

    allowDownloadFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 2},
        { type: 'Operator', level: 3},
        { type: 'Workshop User', level: 1},
      ]);
    }
  // #endregion

  // #region Functions required for the CSV Download 
    getDataForCSV() {
      return this.searchResults.reduce((a: any, b: any) => {
        let newItem = b;
        newItem = Object.assign({}, b, {  });

        return a.concat(newItem);
      }, []);
    }

    getFieldsForCSV() {
      return {
        'Vehicle 1': 'vehicle1Code',
        'Vehicle 2': 'vehicle2Code',        
        'Job Type': 'jobType',
        'Secondary Job Type': 'secondaryJobType',
        'Opened By': 'createdBy',
        'Start Date': 'startDate',
        'Original ETA': 'originalEta',
        'Adjusted ETA': 'originalEta',
        'Adjustment Reason': 'adjustedEtaReason',
        'Adjusted By': 'adjustedBy',
        'Closed At': 'closedAt',
        'Closed By': 'closedBy',
      };
    }
  // #endregion
}
